import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import { trackCalendlyScheduled, trackContactClick, trackGenerateLead } from '../lib/tracking';
import { submitLead } from '../lib/leads';
import { COMPANY } from '../lib/constants';
import { useBrochure } from '../hooks/useBrochure';
import { CheckCircle, Download, Mail, MessageCircle, Phone } from 'lucide-react';

const CALENDLY_URL =
  'https://calendly.com/praatuit/kennismaking-punt-uit?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=0284c7';

function CalendlyWidget() {
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (
        e.origin === 'https://calendly.com' &&
        (e.data as { event?: string } | null)?.event === 'calendly.event_scheduled'
      ) {
        trackCalendlyScheduled();
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;

    const syncIframeHeight = () => {
      const iframe = widget.querySelector('iframe');
      if (iframe) iframe.style.height = '100%';
    };

    syncIframeHeight();
    const observer = new ResizeObserver(syncIframeHeight);
    observer.observe(widget);
    const mutation = new MutationObserver(syncIframeHeight);
    mutation.observe(widget, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutation.disconnect();
    };
  }, []);

  return (
    <div
      ref={widgetRef}
      className="calendly-inline-widget h-full min-h-[700px] lg:min-h-0 rounded-2xl overflow-hidden border bg-white"
      data-url={CALENDLY_URL}
      style={{ minWidth: '320px', height: '100%' }}
    />
  );
}

export function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [question, setQuestion] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const { openBrochure } = useBrochure();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    const ok = await submitLead(
      {
        Naam: name,
        'E-mailadres': email,
        Telefoonnummer: phone || '-',
        'Vraag of situatie': question,
      },
      { subject: `Contactformulier: bericht van ${name}` }
    );
    if (ok) {
      trackGenerateLead({ formName: 'contact', email, phone });
      setStatus('sent');
    } else {
      setStatus('error');
    }
  }

  return (
    <>
      <SEO title="Contact | PuntUit" description="Neem contact op met PuntUit voor een vrijblijvend gesprek over een externe vertrouwenspersoon. Bel, mail of WhatsApp — we reageren snel." canonicalUrl="/contact" />
      <SchemaMarkup
        type="localBusiness"
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' },
        ]}
      />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold">Stel je vraag. We reageren snel.</h1>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container max-w-6xl grid lg:grid-cols-2 gap-x-8 lg:gap-x-12 gap-y-6 items-stretch">
            <div>
              <h2 className="text-3xl font-bold mb-2">Plan direct een kennismakingsgesprek</h2>
              <p className="text-gray-600">Kies hieronder een moment dat jou uitkomt. Het gesprek is vrijblijvend en kosteloos.</p>
            </div>
            <div className="hidden lg:block" aria-hidden="true" />
            <div className="overflow-hidden lg:h-[700px]">
              <CalendlyWidget />
            </div>
            <div className="flex flex-col gap-4 lg:h-[700px]">
              <a
                href="tel:+31851071256"
                className="flex flex-1 flex-col p-5 rounded-xl border border-gray-200 bg-gray-50 text-inherit hover:border-primary-300 hover:bg-primary-50/50 hover:text-inherit"
                onClick={() => trackContactClick({ method: 'phone', location: 'contact_card' })}
              >
                <Phone className="w-5 h-5 text-primary-700 mb-3" />
                <p className="font-semibold">Bellen</p>
                <p className="text-gray-600 text-sm">
                  085 - 107 12 56
                  <br />Bereikbaar op werkdagen van 09:00 tot 17:00.
                </p>
              </a>
              <a
                href="mailto:info@puntuit.nl"
                className="flex flex-1 flex-col p-5 rounded-xl border border-gray-200 bg-gray-50 text-inherit hover:border-primary-300 hover:bg-primary-50/50 hover:text-inherit"
                onClick={() => trackContactClick({ method: 'email', location: 'contact_card' })}
              >
                <Mail className="w-5 h-5 text-primary-700 mb-3" />
                <p className="font-semibold">Mailen</p>
                <p className="text-gray-600 text-sm">
                  info@puntuit.nl
                  <br />Reactie binnen één werkdag.
                </p>
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 flex-col p-5 rounded-xl border border-gray-200 bg-gray-50 text-inherit hover:border-primary-300 hover:bg-primary-50/50 hover:text-inherit"
                onClick={() => trackContactClick({ method: 'whatsapp', location: 'contact_card' })}
              >
                <MessageCircle className="w-5 h-5 text-primary-700 mb-3" />
                <p className="font-semibold">WhatsApp</p>
                <p className="text-gray-600 text-sm">
                  {COMPANY.whatsapp}
                  <br />Stuur een bericht. We nemen contact op.
                </p>
              </a>
              <button
                type="button"
                onClick={openBrochure}
                className="flex flex-1 flex-col items-start text-left p-5 rounded-xl border border-gray-200 bg-gray-50 text-inherit hover:border-primary-300 hover:bg-primary-50/50 cursor-pointer"
              >
                <Download className="w-5 h-5 text-primary-700 mb-3" />
                <p className="font-semibold">Brochure downloaden</p>
                <p className="text-gray-600 text-sm">
                  Onze aanpak en werkwijze in één PDF.
                  <br />Laat je gegevens achter, de download start direct.
                </p>
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white pb-16 md:pb-24">
          <div className="container max-w-6xl">
            <h2 className="text-3xl font-bold mb-6">Liever eerst je vraag stellen?</h2>
            {status === 'sent' ? (
              <div className="bg-white p-8 rounded-2xl border" role="status">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Bedankt voor je bericht!</p>
                    <p className="text-gray-600">
                      We hebben je vraag ontvangen en reageren binnen één werkdag op {email}.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4 bg-white p-8 rounded-2xl border">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1 text-gray-700">Jouw naam</label>
                <input
                  id="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Jouw naam"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1 text-gray-700">Jouw e-mailadres</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Jouw e-mailadres"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-1 text-gray-700">Jouw telefoonnummer (optioneel)</label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Jouw telefoonnummer"
                />
              </div>
              <div>
                <label htmlFor="question" className="block text-sm font-medium mb-1 text-gray-700">Jouw vraag of situatie</label>
                <textarea
                  id="question"
                  required
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl min-h-36"
                  placeholder="Jouw vraag of situatie"
                />
              </div>
              <button className="btn-primary disabled:opacity-60" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Versturen…' : 'Verstuur'}
              </button>
              {status === 'error' && (
                <p className="text-sm text-red-600" role="alert">
                  Het versturen is helaas niet gelukt. Probeer het opnieuw, of mail ons direct op{' '}
                  <a href="mailto:info@puntuit.nl" className="underline">info@puntuit.nl</a>.
                </p>
              )}
              <p className="text-sm text-gray-500">
                Je gegevens worden vertrouwelijk behandeld. Zie onze{' '}
                <Link to="/privacyverklaring" className="underline underline-offset-2">privacyverklaring</Link>.
              </p>
            </form>
            )}
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container max-w-4xl">
            <h2 className="text-2xl font-bold mb-6">Vestiging / Factuurgegevens</h2>
            <div className="grid md:grid-cols-2 gap-8 text-gray-700">
              <div>
                <p className="font-semibold mb-1">Bezoekadres:</p>
                <p>Stammerhove 3</p>
                <p>1112 VA, Diemen</p>
              </div>
              <div>
                <p className="font-semibold mb-1">Postadres:</p>
                <p>Vleerstraat 78</p>
                <p>2513 VJ, Den Haag</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200 text-gray-700 space-y-1">
              <p>
                <a
                  href="tel:+31851071256"
                  className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
                  onClick={() => trackContactClick({ method: 'phone', location: 'vestiging' })}
                >
                  085 - 107 12 56
                </a>
              </p>
              <p>
                <a
                  href="mailto:info@puntuit.nl"
                  className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
                  onClick={() => trackContactClick({ method: 'email', location: 'vestiging' })}
                >
                  info@puntuit.nl
                </a>
              </p>
              <p>Kvk: 87786516</p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
